import { useEffect, useRef, useState, useCallback } from 'react';
import { ChecklistDiscussion, WSIncoming, WSOutgoing } from '@/types/discussion';
import { env } from '@/utils/constants/env';

interface UseTenantDiscussionWebSocketParams {
  tenantId: number;
  userId: number;
  userName: string;
  userRole: string;
  enabled?: boolean;
}

interface UseTenantDiscussionWebSocketReturn {
  messages: ChecklistDiscussion[];
  isConnected: boolean;
  sendMessage: (message: string, parentId?: number | null) => void;
  editMessage: (discussionId: number, message: string) => void;
  deleteMessage: (discussionId: number) => void;
  markAsRead: (discussionId: number) => void;
  error: string | null;
  reconnect: () => void;
  addMessage: (message: ChecklistDiscussion) => void;
  setMessages: React.Dispatch<React.SetStateAction<ChecklistDiscussion[]>>;
}

/**
 * WebSocket hook for tenant discussions (without instanceId dependency)
 */
export const useTenantDiscussionWebSocket = ({
  tenantId,
  userId,
  userName,
  userRole,
  enabled = true,
}: UseTenantDiscussionWebSocketParams): UseTenantDiscussionWebSocketReturn => {
  const ws = useRef<WebSocket | null>(null);
  const [messages, setMessages] = useState<ChecklistDiscussion[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const getWebSocketUrl = useCallback(() => {
    // Convert http/https to ws/wss
    const wsProtocol = env.apiTasklistBaseUrl.startsWith('https') ? 'wss' : 'ws';
    const baseUrl = env.apiTasklistBaseUrl.replace(/^https?:\/\//, '');

    // Tenant discussion WebSocket endpoint (no instanceId)
    return `${wsProtocol}://${baseUrl}/ws/compro/discussions/${tenantId}?user_id=${userId}&user_name=${encodeURIComponent(
      userName,
    )}&user_role=${userRole}`;
  }, [tenantId, userId, userName, userRole]);

  const connect = useCallback(() => {
    if (!enabled || ws.current?.readyState === WebSocket.OPEN) return;

    try {
      const wsUrl = getWebSocketUrl();
      console.log('[TenantDiscussionWS] Connecting to:', wsUrl);

      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('[TenantDiscussionWS] Connected successfully');
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.current.onmessage = event => {
        try {
          const data: WSIncoming = JSON.parse(event.data);
          console.log('[TenantDiscussionWS] Received:', data);

          if (data.type === 'message') {
            setMessages(prev => [...prev, data.data]);
          } else if (data.type === 'edit') {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === data.data.id
                  ? {
                      ...msg,
                      message: data.data.message,
                      is_edited: data.data.is_edited,
                      updated_at: data.data.updated_at,
                    }
                  : msg,
              ),
            );
          } else if (data.type === 'delete') {
            // Hapus pesan dari list ketika ada konfirmasi dari server
            setMessages(prev => prev.filter(msg => msg.id !== data.data.id));
          } else if (data.type === 'error') {
            console.error('[TenantDiscussionWS] Server error:', data.error);
            setError(data.error);
            setTimeout(() => setError(null), 5000);
          }
        } catch (err) {
          console.error('[TenantDiscussionWS] Failed to parse message:', err);
        }
      };

      ws.current.onerror = () => {
        console.error('[TenantDiscussionWS] Connection error');
      };

      ws.current.onclose = event => {
        console.log('[TenantDiscussionWS] Disconnected:', event.code, event.reason);
        setIsConnected(false);

        if (enabled && event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          console.log(
            `[TenantDiscussionWS] Reconnecting in ${delay}ms (attempt ${
              reconnectAttemptsRef.current + 1
            }/${maxReconnectAttempts})...`,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        } else if (event.code !== 1000 && reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setError('Failed to connect to chat server after multiple attempts');
        }
      };
    } catch (err) {
      console.error('[TenantDiscussionWS] Connection failed:', err);
      setError('Failed to establish WebSocket connection');
    }
  }, [enabled, getWebSocketUrl]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (ws.current) {
      ws.current.close(1000, 'Component unmounted');
      ws.current = null;
    }
  }, []);

  const sendMessage = useCallback((message: string, parentId?: number | null) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      const payload: WSOutgoing = {
        type: 'message',
        message,
        parent_id: parentId || null,
      };

      console.log('[TenantDiscussionWS] Sending message:', payload);
      ws.current.send(JSON.stringify(payload));
    } else {
      console.error('[TenantDiscussionWS] Cannot send message: not connected');
      setError('Not connected to chat server');
    }
  }, []);

  const editMessage = useCallback((discussionId: number, message: string) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      const payload: WSOutgoing = {
        type: 'edit',
        discussion_id: discussionId,
        message,
      };

      ws.current.send(JSON.stringify(payload));
    } else {
      setError('Not connected to chat server');
    }
  }, []);

  const deleteMessage = useCallback((discussionId: number) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      // Optimistic update - langsung hapus dari UI
      setMessages(prev => prev.filter(msg => msg.id !== discussionId));

      const payload: WSOutgoing = {
        type: 'delete',
        discussion_id: discussionId,
      };

      ws.current.send(JSON.stringify(payload));
    } else {
      setError('Not connected to chat server');
    }
  }, []);

  const markAsRead = useCallback((discussionId: number) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      const payload: WSOutgoing = {
        type: 'mark_read',
        discussion_id: discussionId,
      };

      ws.current.send(JSON.stringify(payload));
    }
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    setError(null);
    connect();
  }, [connect, disconnect]);

  const addMessage = useCallback((message: ChecklistDiscussion) => {
    setMessages(prev => [...prev, message]);
  }, []);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    messages,
    isConnected,
    sendMessage,
    editMessage,
    deleteMessage,
    markAsRead,
    error,
    reconnect,
    addMessage,
    setMessages,
  };
};
